import { router } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Mail, Youtube, MessageCircle, ExternalLink } from "lucide-react"
import { useState } from "react"

interface IntegrationCardProps {
  type: string
  name: string
  description: string
  host_label?: string | null
  host_placeholder?: string | null
  credential_label: string
  credential_placeholder: string
  credential_help: string
  credential_help_url: string
  status: "connected" | "disconnected"
  connected_at: string | null
  connection_metadata: Record<string, unknown>
}

const ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  mimeo: Mail,
  youtube: Youtube,
  discord: MessageCircle,
}

export function IntegrationCard({
  type,
  name,
  description,
  host_label,
  host_placeholder,
  credential_label,
  credential_placeholder,
  credential_help,
  credential_help_url,
  status,
  connected_at,
  connection_metadata,
}: IntegrationCardProps) {
  const [host, setHost] = useState("")
  const [credential, setCredential] = useState("")
  const [connecting, setConnecting] = useState(false)
  const [disconnectOpen, setDisconnectOpen] = useState(false)

  const Icon = ICONS[type] || Mail
  const isConnected = status === "connected"

  function handleConnect() {
    setConnecting(true)
    router.post(
      `/admin/integrations/${type}/connect`,
      host_label ? { credential, host } : { credential },
      {
        preserveScroll: true,
        onFinish: () => {
          setConnecting(false)
          setCredential("")
          setHost("")
        },
      }
    )
  }

  function handleDisconnect() {
    router.delete(`/admin/integrations/${type}/disconnect`, {
      preserveScroll: true,
      onFinish: () => setDisconnectOpen(false),
    })
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="bg-muted flex size-10 items-center justify-center rounded-lg">
            <Icon className="size-5" />
          </div>
          <div className="flex-1">
            <CardTitle className="text-base">{name}</CardTitle>
          </div>
          <Badge variant={isConnected ? "default" : "secondary"}>
            {isConnected ? "Connected" : "Disconnected"}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-muted-foreground text-sm">{description}</p>

        {isConnected ? (
          <>
            {connected_at && (
              <p className="text-muted-foreground text-sm">
                Connected {new Date(connected_at).toLocaleDateString()}
              </p>
            )}
            {Object.keys(connection_metadata).length > 0 && (
              <div className="bg-muted space-y-1 rounded-md p-3">
                {Object.entries(connection_metadata).map(([key, value]) => (
                  <div key={key} className="flex gap-2 text-sm">
                    <span className="text-muted-foreground">{key}:</span>
                    <span>{String(value)}</span>
                  </div>
                ))}
              </div>
            )}
            <Dialog open={disconnectOpen} onOpenChange={setDisconnectOpen}>
              <DialogTrigger asChild>
                <Button variant="destructive" size="sm">
                  Disconnect
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Disconnect {name}</DialogTitle>
                  <DialogDescription>
                    Are you sure you want to disconnect {name}? Sources using this integration
                    will stop receiving updates.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setDisconnectOpen(false)}>
                    Cancel
                  </Button>
                  <Button variant="destructive" onClick={handleDisconnect}>
                    Disconnect
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </>
        ) : (
          <div className="space-y-3">
            {host_label && (
              <div className="space-y-2">
                <Label htmlFor={`host-${type}`}>{host_label}</Label>
                <Input
                  id={`host-${type}`}
                  type="url"
                  placeholder={host_placeholder || ""}
                  value={host}
                  onChange={(e) => setHost(e.target.value)}
                />
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor={`credential-${type}`}>{credential_label}</Label>
              <Input
                id={`credential-${type}`}
                type="password"
                placeholder={credential_placeholder}
                value={credential}
                onChange={(e) => setCredential(e.target.value)}
              />
              <p className="text-muted-foreground text-xs">
                {credential_help}{" "}
                <a
                  href={credential_help_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary inline-flex items-center gap-1 underline underline-offset-2 hover:text-primary/80"
                >
                  Open<ExternalLink className="size-3" />
                </a>
              </p>
            </div>
            <Button
              size="sm"
              onClick={handleConnect}
              disabled={connecting || !credential.trim() || (!!host_label && !host.trim())}
            >
              {connecting ? "Connecting..." : "Connect"}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
